import { Component, Input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageService } from 'primeng/api';
import { ProductRatingService, ArkifiHubProductRatingDto, CreateArkifiHubProductRatingDto, ArkifiHubProductRatingSummaryDto } from '../../services/product-rating.service';
import { AuthService } from '../../services/auth.service';
import { SettingsService } from '../../services/settings.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-product-rating',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, ProgressSpinnerModule],
  providers: [MessageService],
  templateUrl: './product-rating.component.html',
  styleUrl: './product-rating.component.css'
})
export class ProductRatingComponent implements OnInit {
  @Input() productId!: string;
  @Input() platformId!: string;
  @Input() showCreateForm: boolean = false;

  // Rating data
  ratingSummary = signal<ArkifiHubProductRatingSummaryDto | null>(null);
  ratings = signal<ArkifiHubProductRatingDto[]>([]);
  canRate = signal(false);
  isLoadingSummary = signal(false);
  isLoadingRatings = signal(false);

  // Create rating form
  newRating: CreateArkifiHubProductRatingDto = {
    productId: '',
    platformId: '',
    environmentType: 1, // Test = 1, Live = 2 (matches EnviromentType enum)
    rating: 5,
    review: '',
    reviewTitle: '',
    qualityRating: 5,
    valueRating: 5,
    shippingRating: 5,
    customerServiceRating: 5
  };

  isSubmittingRating = signal(false);
  showRatingForm = signal(false);

  constructor(
    private productRatingService: ProductRatingService,
    private authService: AuthService,
    private settingsService: SettingsService,
    private router: Router,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    if (this.productId && this.platformId) {
      this.loadRatingData();
    }
  }

  private loadRatingData(): void {
    this.loadRatingSummary();
    this.loadRatings();
    this.checkCanRate();
  }

  private loadRatingSummary(): void {
    this.isLoadingSummary.set(true);
    const environment = this.settingsService.getEnvironment();
    
    this.productRatingService.getProductRatingSummary(this.productId, this.platformId, environment).subscribe({
      next: (response) => {
        if (response.success) {
          this.ratingSummary.set(response.data);
        }
        this.isLoadingSummary.set(false);
      },
      error: (error) => {
        console.error('Error loading rating summary:', error);
        this.isLoadingSummary.set(false);
      }
    });
  }

  private loadRatings(): void {
    this.isLoadingRatings.set(true);
    const environment = this.settingsService.getEnvironment();
    
    this.productRatingService.getProductRatings(this.productId, this.platformId, environment, 1, 10).subscribe({
      next: (response) => {
        if (response.success) {
          this.ratings.set(response.data);
        }
        this.isLoadingRatings.set(false);
      },
      error: (error) => {
        console.error('Error loading ratings:', error);
        this.isLoadingRatings.set(false);
      }
    });
  }

  private checkCanRate(): void {
    if (!this.authService.isAuthenticated()) {
      this.canRate.set(false);
      return;
    }

    const environment = this.settingsService.getEnvironment();
    this.productRatingService.canRateProduct(this.productId, this.platformId, environment).subscribe({
      next: (response) => {
        if (response.success) {
          this.canRate.set(response.data);
        }
      },
      error: (error) => {
        console.error('Error checking rating eligibility:', error);
        this.canRate.set(false);
      }
    });
  }

  showCreateRatingForm(): void {
    if (!this.authService.isAuthenticated()) {
      this.onStarsClick();
      return;
    }

    const environment = this.settingsService.getEnvironment();
    // Convert 'Test'/'Live' to enum integer: Test = 1, Live = 2
    const environmentTypeValue = environment === 'Live' ? 2 : 1;

    this.newRating = {
      productId: this.productId,
      platformId: this.platformId,
      environmentType: environmentTypeValue, // Send as integer to match backend enum
      rating: 5,
      review: '',
      reviewTitle: '',
      qualityRating: 5,
      valueRating: 5,
      shippingRating: 5,
      customerServiceRating: 5
    };
    this.showRatingForm.set(true);
  }

  hideCreateRatingForm(): void {
    this.showRatingForm.set(false);
  }

  setRating(rating: number): void {
    this.newRating.rating = rating;
  }

  setDetailRating(type: 'quality' | 'value' | 'shipping' | 'customerService', rating: number): void {
    switch (type) {
      case 'quality':
        this.newRating.qualityRating = rating;
        break;
      case 'value':
        this.newRating.valueRating = rating;
        break;
      case 'shipping':
        this.newRating.shippingRating = rating;
        break;
      case 'customerService':
        this.newRating.customerServiceRating = rating;
        break;
    }
  }

  submitRating(): void {
    if (!this.newRating.productId || this.newRating.rating < 1 || this.newRating.rating > 5) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Rating',
        detail: 'Please provide a valid rating between 1 and 5 stars',
        life: 4000
      });
      return;
    }

    this.isSubmittingRating.set(true);
    this.productRatingService.createRating(this.newRating).subscribe({
      next: (response) => {
        if (response.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Review Submitted!',
            detail: 'Your review has been submitted successfully and is now live!',
            life: 4000
          });
          this.showRatingForm.set(false);
          this.canRate.set(false);
          this.loadRatingData();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Submission Failed',
            detail: response.message || 'An error occurred while submitting your review.',
            life: 7000
          });
        }
        this.isSubmittingRating.set(false);
      },
      error: (error) => {
        console.error('Error submitting rating:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Submission Failed',
          detail: 'Failed to submit your review. Please check your connection and try again.',
          life: 7000
        });
        this.isSubmittingRating.set(false);
      }
    });
  }

  // Utility methods
  getStarArray(rating: number): number[] {
    return Array.from({ length: rating }, (_, i) => i + 1);
  }

  getStarClass(starIndex: number, currentRating: number): string {
    if (starIndex <= currentRating) {
      return 'star-filled';
    }
    return 'star-empty';
  }

  getRatingPercentage(count: number): number {
    const summary = this.ratingSummary();
    if (!summary || summary.totalRatings === 0) {
      return 0;
    }
    return Math.round((count / summary.totalRatings) * 100);
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  onStarsClick(): void {
    if (!this.authService.isAuthenticated()) {
      const currentUrl = this.router.url;
      this.router.navigate(['/signin'], {
        queryParams: {
          returnUrl: currentUrl,
          reason: 'rate-product'
        }
      });
      return;
    }

    if (this.canRate()) {
      this.showCreateRatingForm();
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot Rate Product',
        detail: 'Unable to rate this product at the moment. Please try again.',
        life: 3000
      });
    }
  }

  onRatingBarClick(rating: number): void {
    const reviewsSection = document.querySelector('.ratings-list');
    if (reviewsSection) {
      reviewsSection.scrollIntoView({ behavior: 'smooth' });
    }
  }
}
